import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { colors, glyphs } from '../../Styles';
import { ButtonList, EventNotePopover, Overlay } from '..';
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types } from '../../Definitions';
import { useEditableColumnConstructor } from '../Tables/_Tables/useEditableColumnConstructor';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ReactiveTable } from '../Tables/reactiveTableRenderer';

import AddEventModal from './Modals/addEventModal';
import { useAppSelector } from '../../Store';
import { Graph_QL } from '../../Services';
import { useAnimal } from '../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal';

interface Events_Props {
  selectedAnimal: number
}
/**
 * Events
 * @param selectedAnimal
 * @returns
 */
export default function Events({ selectedAnimal }:Events_Props) {
  const {t} = useTranslation()
  const [addEventModal, setAddEventModal] = useState(false)
  const [editTable, setEditTable] = useState(false);
  
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]); const { call: getAnimalsInfo, data: animalsState} = useAnimal(connection);
  
  const animal = useMemo(() => {
    return animalsState && animalsState[domain] ? animalsState[domain].data.find((animal) => animal.animal_id === selectedAnimal) : undefined
  },[animalsState, domain, selectedAnimal])
  
  useEffect(() => {
    if (selectedAnimal && selectedAnimal !== 0) {
      getAnimalsInfo({ids:[selectedAnimal], ifFeedCurveBaseInfoOnly: false, ifSkipLastTenDaysConsumption: false}, connection)
    }
  }, [connection, getAnimalsInfo, selectedAnimal])


  // Date formatting function
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const tableData = useMemo(() => {
    return animal && animal.event && animal.event.length > 0
      ? animal.event.map((event, index, arr) => {
          const notes = event.note || "";
  
          return { 
            eventName: event.eventName, 
            eventDate: formatDate(new Date(event.timestamp)),
            eventNotes: event.note ? (
              <EventNotePopover 
                eventName={event.eventName} 
                eventNote={notes} 
                glyphColor={colors.greyDarkPrimary} 
              />
            ) : <></>,
            id: event.eventNo 
          };
        })
      : [];
  }, [animal]);

  // #region | Events Table
  const accessorData = useMemo(() => ([
    {headerValue: t("animalPages.animal.eventName", {defaultValue: "name"}), accessor: "eventName", type: ListData_ValueTypes.String},
    {headerValue: t("animalPages.animal.eventDate", {defaultValue: "date"}), accessor: "eventDate", type: ListData_ValueTypes.String},
    {headerValue: t("animalPages.animal.eventNote", {defaultValue: "note"}), accessor: "eventNotes", type: ListData_ValueTypes.Div},
  ]),[t])
  
  const columns = useEditableColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.primaryTable, 
    canEdit: editTable 
  });
  
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
  })
  
  const reactiveTable = <>{ReactiveTable({table, tableType: TableStyles_Types.primaryTable, emptyText: "Empty text", renderFooter: false})}</>
  // #endregion

  return ( <>
    { editTable ? <Overlay/> : <></> }
    <div style={{position: "relative", height: "calc(100% - 50px)"}}>
        <div style={{display: "flex", position: "relative", justifyContent: "end", padding: "0px 20px", zIndex: editTable? "9999" : "0"}}>
        { editTable 
          ? <ButtonList
              contents={[t("buttons.save", {defaultValue: "Save"}), t("buttons.cancel", {defaultValue: "cancel"}), t("buttons.add", {defaultValue: "add"}), t("buttons.edit", {defaultValue: "edit"})]}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Add({}), glyphs.Edit({}) ]}
              onClick={(i => {
                if (i === 0) {} // TODO: Save to db
                if (i === 1) {setEditTable(false)}
              })}
              disabled={[false, false, true, true]}
              overrides={(index) => ({
                baseButton: {backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary},
                hoverStyles: {backgroundColor: colors.greyPrimary},
                disabledStyles: {opacity: index === 3 ? 1 : 0.5},
              })}
            /> 
          : <ButtonList
              contents={[t("buttons.add", {defaultValue: "add"}), t("buttons.edit", {defaultValue: "edit"})]}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[ glyphs.Add({}), glyphs.Edit({}) ]}
              onClick={(i => {
                if (i === 0) {setAddEventModal(true)} // TODO: Add new event
                if (i === 1) {setEditTable(true)}
              })}
          />
        }
      </div>
      <div style={{height: "100%", boxSizing: "border-box", position: "relative", zIndex: editTable? "9999" : "0", backgroundColor: colors.greyLightSecondary, border: "5px solid "+colors.greyPrimary, borderRadius: "10px", overflowY: "hidden"}}>
        {reactiveTable}
      </div>
    </div>
    <AddEventModal isModalOpen={addEventModal} closeModal={() => setAddEventModal(false)}/>
  </> );
}